// image-compression-and-integration.js
// Utility to compress/resize images client-side and example integration with HousingForm (React + react-hook-form)

/**
 * compressImage(file, options) -> Promise<File>
 * - file: File (image)
 * - options: { maxSizeMB = 1, maxWidth = 1920, maxHeight = 1920, mimeType = 'image/jpeg' }
 * Returns: compressed File (same name, new type = jpeg)
 */
export async function compressImage(file, options = {}) {
  const { maxSizeMB = 1, maxWidth = 1920, maxHeight = 1920, mimeType = 'image/jpeg' } = options;
  const targetBytes = maxSizeMB * 1024 * 1024;

  if (!file.type.startsWith('image/')) throw new Error('Not an image file');

  // read file to dataURL
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });

  // load image
  const img = await new Promise((resolve, reject) => {
    const image = new Image();
    image.onerror = reject;
    image.onload = () => resolve(image);
    image.src = dataUrl;
  });

  // calculate initial dimensions keeping aspect ratio
  let { width, height } = img;
  const aspect = width / height;
  if (width > maxWidth) {
    width = maxWidth;
    height = Math.round(width / aspect);
  }
  if (height > maxHeight) {
    height = maxHeight;
    width = Math.round(height * aspect);
  }

  // helper to get blob from canvas at quality
  const canvasToBlob = (canvas, quality) => new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mimeType, quality);
  });

  // create canvas once and reuse
  let canvas = document.createElement('canvas');
  let ctx = canvas.getContext('2d');

  // iterative strategy:
  // 1) try decreasing quality values while keeping dimensions
  // 2) if still bigger, reduce dimensions (scale down) and retry qualities

  const qualitySteps = [0.92, 0.85, 0.75, 0.65, 0.55, 0.45, 0.35, 0.25];
  let currentWidth = width;
  let currentHeight = height;

  for (let scaleAttempt = 0; scaleAttempt < 8; scaleAttempt++) {
    canvas.width = Math.max(1, Math.round(currentWidth));
    canvas.height = Math.max(1, Math.round(currentHeight));
    // draw with smooth scaling
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    for (let q of qualitySteps) {
      // eslint-disable-next-line no-await-in-loop
      const blob = await canvasToBlob(canvas, q);
      if (!blob) continue;
      if (blob.size <= targetBytes) {
        // success
        return new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: mimeType });
      }
    }

    // if not small enough, downscale dimensions and try again
    currentWidth = currentWidth * 0.8;
    currentHeight = currentHeight * 0.8;
  }

  // last attempt: return smallest we could make with lowest quality
  canvas.width = Math.max(1, Math.round(currentWidth));
  canvas.height = Math.max(1, Math.round(currentHeight));
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const finalBlob = await canvasToBlob(canvas, 0.25);
  return new File([finalBlob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: mimeType });
}

// ----------------------
// Example: integration snippet for HousingForm (React)
// Add these changes to HousingForm.js:

/*
1) import at top of HousingForm.js:

import { compressImage } from '../utils/image-compression-and-integration';

2) Add state to store compressed file and preview URL:

const [photoFile, setPhotoFile] = useState(null);
const [photoPreview, setPhotoPreview] = useState(null); // replace existing

3) Replace handlePhotoChange with:

const handlePhotoChange = async (e) => {
  const file = e.target.files?.[0];
  if (!file) {
    setPhotoFile(null);
    setPhotoPreview(null);
    return;
  }

  try {
    // compress to <= 1 MB
    const compressed = await compressImage(file, { maxSizeMB: 1, maxWidth: 1600, maxHeight: 1600 });
    setPhotoFile(compressed);
    // show preview from blob URL (faster than base64)
    const url = URL.createObjectURL(compressed);
    setPhotoPreview(url);
  } catch (err) {
    console.error('Gagal kompres gambar:', err);
    // fallback to original
    setPhotoFile(file);
    const url = URL.createObjectURL(file);
    setPhotoPreview(url);
  }
};

4) Update onSubmit to append compressed file if present instead of raw input:

if (data.photo_before_repair && photoFile) {
  formData.append('photo_before_repair', photoFile, photoFile.name);
} else if (data.photo_before_repair && data.photo_before_repair[0]) {
  // fallback if user didn't go through our handler
  formData.append('photo_before_repair', data.photo_before_repair[0]);
}

// and make sure you don't append the raw FileList elsewhere.

5) Clean up object URLs when component unmounts or when changing preview to avoid memory leaks:

useEffect(() => {
  return () => {
    if (photoPreview && photoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(photoPreview);
    }
  };
}, [photoPreview]);

*/

// That's it — utility + example integration. You can tweak maxWidth/maxHeight and qualitySteps to tune speed vs size.
